import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LogOut, Inbox, Mail, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Admin() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [inquiries, setInquiries] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([api.get("/inquiries"), api.get("/inquiries/stats")])
            .then(([inq, st]) => {
                setInquiries(inq.data);
                setStats(st.data);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const doLogout = () => {
        logout();
        navigate("/admin/login");
    };

    const fmt = (iso) => {
        try {
            return new Date(iso).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    };

    return (
        <div className="min-h-screen bg-abyss grain text-foreground">
            {/* Top bar */}
            <header className="border-b border-border bg-surface">
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="h-9 w-9 bg-safety flex items-center justify-center font-heading font-black text-white text-lg">
                            K
                        </span>
                        <span className="font-heading font-extrabold tracking-tight">
                            KEVA OPS · INQUIRIES
                        </span>
                    </div>
                    <div className="flex items-center gap-6">
                        <span className="hidden sm:block font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel">
                            {user?.email}
                        </span>
                        <button
                            data-testid="logout-btn"
                            onClick={doLogout}
                            className="inline-flex items-center gap-2 border border-border hover:border-safety text-foreground font-mono-tech text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 transition-colors"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
                {/* Stat tiles */}
                <div className="grid grid-cols-2 lg:grid-cols-3 border-l border-t border-border mb-12">
                    <div className="border-r border-b border-border p-8">
                        <div className="flex items-center gap-2 text-steel mb-3">
                            <Inbox className="h-4 w-4 text-safety" />
                            <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em]">
                                Total inquiries
                            </span>
                        </div>
                        <p className="font-heading font-black text-5xl">{stats.total}</p>
                    </div>
                    <div className="border-r border-b border-border p-8">
                        <div className="flex items-center gap-2 text-steel mb-3">
                            <Mail className="h-4 w-4 text-safety" />
                            <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em]">
                                New / unread
                            </span>
                        </div>
                        <p className="font-heading font-black text-5xl">{stats.new}</p>
                    </div>
                    <div className="border-r border-b border-border p-8 hidden lg:block">
                        <div className="flex items-center gap-2 text-steel mb-3">
                            <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em]">
                                Status
                            </span>
                        </div>
                        <p className="font-heading font-black text-5xl text-safety">LIVE</p>
                    </div>
                </div>

                {/* Table */}
                <div className="border border-border bg-surface">
                    <div className="px-6 py-4 border-b border-border">
                        <h2 className="font-mono-tech text-xs uppercase tracking-[0.2em] text-steel">
                            Submitted assessment requests
                        </h2>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-24 text-steel">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : inquiries.length === 0 ? (
                        <div className="py-24 text-center text-steel font-mono-tech text-xs uppercase tracking-[0.2em]">
                            No inquiries yet
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid="inquiries-table">
                                <TableHeader>
                                    <TableRow className="border-border hover:bg-transparent">
                                        <TableHead className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-steel">Date</TableHead>
                                        <TableHead className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-steel">Name</TableHead>
                                        <TableHead className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-steel">Company</TableHead>
                                        <TableHead className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-steel">Contact</TableHead>
                                        <TableHead className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-steel">Asset</TableHead>
                                        <TableHead className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-steel">Scope</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {inquiries.map((q) => (
                                        <TableRow key={q.id} data-testid={`inquiry-row-${q.id}`} className="border-border hover:bg-elevated/50">
                                            <TableCell className="font-mono-tech text-xs text-steel whitespace-nowrap">{fmt(q.created_at)}</TableCell>
                                            <TableCell className="font-medium">{q.name}</TableCell>
                                            <TableCell className="text-steel">{q.company || "—"}</TableCell>
                                            <TableCell className="text-steel">
                                                <a href={`mailto:${q.email}`} className="hover:text-safety transition-colors">{q.email}</a>
                                                {q.phone && <div className="text-xs">{q.phone}</div>}
                                            </TableCell>
                                            <TableCell>
                                                {q.asset_type ? (
                                                    <Badge variant="outline" className="border-safety/40 text-safety rounded-none font-mono-tech text-[10px] uppercase">
                                                        {q.asset_type}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-steel">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="max-w-md text-steel text-sm">{q.message}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
